import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { API } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { GLOBAL_DISCLAIMER } from "@/data/content";

export default function InsightArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 25 });

  useSEO(
    article ? `${article.title} | Smart Legal Solutions` : "Legal Insight | Smart Legal Solutions",
    article?.excerpt
  );

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    API.get(`/insights/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm px-5 pt-24">
        <div className="text-center">
          <p className="font-serif text-4xl text-navy">Article Not Available</p>
          <Link to="/legal-insights" data-testid="article-back-btn" className="btn-outline-dark mt-8">Back to Insights</Link>
        </div>
      </div>
    );
  }

  if (!article) {
    return <div className="min-h-screen bg-warm pt-24" data-testid="article-loading" />;
  }

  return (
    <div data-testid="insight-article-page" className="bg-warm">
      <motion.div data-testid="reading-progress-bar" style={{ scaleX: progress }} className="fixed inset-x-0 top-0 z-50 h-0.5 origin-left bg-gold" />
      <div className="mx-auto max-w-3xl px-5 pb-24 pt-32 lg:px-0">
        <Reveal>
          <Link to="/legal-insights" data-testid="article-back-link" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate transition-colors hover:text-gold">
            <ArrowLeft size={12} /> All Insights
          </Link>
          <p className="mono-label-dark mt-10 !text-gold">{article.category}</p>
          <h1 className="mt-4 font-serif text-4xl font-medium leading-[1.12] tracking-tight text-navy sm:text-5xl">
            {article.title}
          </h1>
          <div className="mt-8 flex flex-wrap gap-x-10 gap-y-3 border-y border-navy/10 py-5">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">{article.author}</span>
            {article.designation && <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">{article.designation}</span>}
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">{article.reading_time} min read</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">Last Reviewed {article.date_reviewed}</span>
          </div>
        </Reveal>

        <p className="mt-10 font-serif text-xl italic leading-relaxed text-navy/70">{article.excerpt}</p>

        {(article.sections || []).map((s, i) => (
          <Reveal key={i} className="mt-12">
            <div className="flex items-baseline gap-5">
              <span className="font-mono text-xs tracking-[0.2em] text-gold">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="font-serif text-2xl font-medium text-navy sm:text-3xl">{s.heading}</h2>
            </div>
            <p className="mt-5 pl-0 text-base leading-[1.85] text-navy/65 sm:pl-11">{s.body}</p>
          </Reveal>
        ))}

        {article.sources?.length > 0 && (
          <Reveal className="mt-14">
            <p className="mono-label-dark">Official Sources</p>
            <ul className="mt-4 space-y-3">
              {article.sources.map((s, i) => (
                <li key={i}>
                  <a
                    data-testid={`article-source-${i}`}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 text-sm text-navy/70 transition-colors hover:text-gold"
                  >
                    {s.label} <ArrowUpRight size={13} className="text-gold" />
                  </a>
                </li>
              ))}
            </ul>
          </Reveal>
        )}

        <Reveal className="mt-14 border border-navy/15 bg-ivory p-8">
          <p className="mono-label-dark">Disclaimer</p>
          <p className="mt-4 text-sm leading-relaxed text-navy/60">{GLOBAL_DISCLAIMER} This article reflects the position as generally understood on its review date and may require revision upon a change in law.</p>
        </Reveal>

        {article.related?.length > 0 && (
          <Reveal className="mt-16">
            <p className="mono-label-dark">Related Reading</p>
            <div className="mt-6 space-y-0 border-t border-navy/10">
              {article.related.map((r, i) => (
                <Link
                  key={r.slug}
                  to={`/legal-insights/${r.slug}`}
                  data-testid={`article-related-${i}`}
                  className="group flex items-center justify-between gap-6 border-b border-navy/10 py-6"
                >
                  <span className="font-serif text-xl font-medium text-navy transition-colors group-hover:text-gold">{r.title}</span>
                  <ArrowUpRight size={16} className="shrink-0 text-slate transition-colors group-hover:text-gold" />
                </Link>
              ))}
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
}
